import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { FaBook, FaChartLine, FaPlay, FaCheckCircle, FaClock } from 'react-icons/fa';
import configData from '../../configure';
import './SubjectView.css';

const SubjectView = ({ orgId }) => {
  // Changed from subjectId to courseCode to match the route parameter
  const { courseCode } = useParams();
  const { token, isLoading, isValidated } = useAuth();
  const [subject, setSubject] = useState(null);
  const [activeTab, setActiveTab] = useState('course-view');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseProgress, setCourseProgress] = useState({
    completedTopics: 0,
    totalTopics: 0,
    overallProgress: 0,
    topicProgress: []
  });

  // Fetch subject details
  const fetchSubjectDetails = async () => {
    if (isLoading || !isValidated) {
      return;
    }

    if (!token) {
      setError('No authentication token available');
      setLoading(false);
      return;
    }

    if (!orgId) {
      setLoading(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${configData.GET_SUBJECTS_BY_ORG}/${orgId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': token,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      
      // Debug: Log the received data
      console.log('Received data:', data);
      console.log('Looking for courseCode:', courseCode);
      
      // Validate that data is an array
      if (!Array.isArray(data)) {
        throw new Error('Invalid data format received from API');
      }
      
      // Find the specific subject by courseCode instead of courseId
      const subjectData = data.find(course => {
        console.log('Checking course:', course.courseCode, 'against', courseCode);
        return course.courseCode === courseCode;
      });
      
      if (!subjectData) {
        throw new Error(`Subject with course code "${courseCode}" not found`);
      }

      console.log('Found subject data:', subjectData);

      // Validate courseDetails exists - FIXED: Added null checks
      if (!subjectData.courseDetails) {
        console.error('Course details missing:', subjectData);
        throw new Error('Course details are missing from the API response');
      }

      // FIXED: Added safe access with optional chaining and default values
      const mappedSubject = {
        id: subjectData.courseId || '',
        name: subjectData.courseDetails?.courseName || subjectData.courseName || 'Unknown',
        code: subjectData.courseCode || 'N/A',
        type: subjectData.courseType 
          ? (subjectData.courseType.charAt(0).toUpperCase() + subjectData.courseType.slice(1))
          : 'Unknown',
        description: subjectData.courseDetails?.courseDescription || subjectData.courseDescription || 'No description available',
        topics: Array.isArray(subjectData.courseDetails?.topics) ? subjectData.courseDetails.topics : []
      };

      console.log('Mapped subject:', mappedSubject);
      setSubject(mappedSubject);
      
      // Initialize dynamic course progress data from APIs
      let completedSubtopics = new Set();
      
      try {
        const planResponse = await fetch(
          `${configData.GET_COURSE_PLAN_BY_COURSE}${mappedSubject.id}`,
          {
            headers: {
              'Accept': 'application/json',
              'Authorization': token,
            },
          }
        );

        if (planResponse.ok) {
          const planData = await planResponse.json();
          const coursePlansList = Array.isArray(planData) ? planData : [planData];
          
          coursePlansList.forEach(plan => {
             if (plan.subtopicsPlanList) {
               plan.subtopicsPlanList.forEach(sub => {
                  if (sub.status === 'Completed') {
                     completedSubtopics.add(sub.subtopicName);
                  }
               });
             }
          });
        }
      } catch (e) {
        console.warn('Could not fetch course plans for progress tracking:', e);
      }

      const completedCount = completedSubtopics.size;

      setCourseProgress({
        completedTopics: completedCount,
        totalTopics: mappedSubject.topics.length,
        overallProgress: mappedSubject.topics.length > 0 ? Math.round((completedCount / mappedSubject.topics.length) * 100) : 0,
        topicProgress: mappedSubject.topics.map((topic, index) => ({
          id: index,
          topic,
          completed: completedSubtopics.has(topic)
        }))
      });

      setLoading(false);

    } catch (error) {
      console.error('Error fetching subject details:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  // Toggle topic completion status
  const toggleTopicCompletion = (topicId) => {
    setCourseProgress(prev => {
      const updatedTopicProgress = prev.topicProgress.map(item => 
        item.id === topicId ? { ...item, completed: !item.completed } : item
      );
      
      const completedCount = updatedTopicProgress.filter(item => item.completed).length;
      const newOverallProgress = prev.totalTopics > 0 ? Math.round((completedCount / prev.totalTopics) * 100) : 0;
      
      return {
        ...prev,
        completedTopics: completedCount,
        overallProgress: newOverallProgress,
        topicProgress: updatedTopicProgress
      };
    });
  };

  useEffect(() => {
    fetchSubjectDetails();
  }, [courseCode, token, orgId, isLoading, isValidated]);

  if (loading) {
    return (
      <div className="subject-view-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading subject details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subject-view-container">
        <div className="error-message">
          <h3>Error loading subject</h3>
          <p>{error}</p>
          <button onClick={fetchSubjectDetails} style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!subject) {
    return (
      <div className="subject-view-container">
        <div className="error-message">
          <h3>Subject not found</h3>
          <p>The requested subject could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subject-view-container">
      <div className="subject-header">
        <div className="subject-title">
          <h1>{subject.name}</h1>
          <div className="subject-meta">
            <span className="subject-code">{subject.code}</span>
            <span className="subject-type">{subject.type}</span>
          </div>
        </div>
      </div>

      <div className="subject-tabs">
        <button
          className={`tab-btn ${activeTab === 'course-view' ? 'active' : ''}`}
          onClick={() => setActiveTab('course-view')}
        >
          <FaBook /> Course View
        </button>
        <button
          className={`tab-btn ${activeTab === 'course-progress' ? 'active' : ''}`}
          onClick={() => setActiveTab('course-progress')}
        >
          <FaChartLine /> Course Progress
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'course-view' && (
          <div className="course-view-tab">
            <div className="course-info-section">
              <div className="info-card">
                <h3>Course Description</h3>
                <p>{subject.description}</p>
              </div>
              
              <div className="info-card">
                <h3>Course Details</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Course Code:</label>
                    <span>{subject.code}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="topics-section">
              <h3>Course Topics</h3>
              {subject.topics.length > 0 ? (
                <div className="topics-grid">
                  {subject.topics.map((topic, index) => (
                    <div key={index} className="topic-card">
                      <div className="topic-header">
                        <span className="topic-number">{index + 1}</span>
                        <h4>{topic}</h4>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-topics">
                  <p>No topics available for this course.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'course-progress' && (
          <div className="course-progress-tab">
            <div className="progress-overview">
              <div className="progress-stats">
                <div className="stat-card">
                  <div className="stat-icon">
                    <FaCheckCircle />
                  </div>
                  <div className="stat-info">
                    <h3>{courseProgress.completedTopics}</h3>
                    <p>Completed Topics</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <FaBook />
                  </div>
                  <div className="stat-info">
                    <h3>{courseProgress.totalTopics}</h3>
                    <p>Total Topics</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <FaChartLine />
                  </div>
                  <div className="stat-info">
                    <h3>{courseProgress.overallProgress}%</h3>
                    <p>Overall Progress</p>
                  </div>
                </div>
              </div>

              <div className="progress-bar-section">
                <h3>Course Progress</h3>
                <div className="progress-bar-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${courseProgress.overallProgress}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{courseProgress.overallProgress}% Complete</span>
                </div>
              </div>
            </div>

            <div className="progress-details">
              <div className="topic-progress-section">
                <h3>Topic Progress</h3>
                <div className="topic-progress-list">
                  {courseProgress.topicProgress.map((item) => (
                    <div 
                      key={item.id} 
                      className={`topic-progress-item ${item.completed ? 'completed' : 'pending'}`}
                    >
                      <div className="topic-progress-info">
                        <span className="topic-status-icon">
                          {item.completed ? <FaCheckCircle /> : <FaClock />}
                        </span>
                        <span className="topic-name">{item.topic}</span>
                      </div>
                      <div className="topic-progress-stats">
                        <span className={`status-badge ${item.completed ? 'completed' : 'pending'}`}>
                          {item.completed ? 'Completed' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubjectView;
